import React from 'react';
import { Sparkles, ThumbsUp, TrendingUp, CalendarRange, MessageCircleQuestion, Briefcase, Loader2 } from 'lucide-react';
import { HiddenBadge } from './ReportBits';

const Block = ({ icon: Icon, title, children, tone = 'text-indigo-600', badge, testid }) => (
  <div className="space-y-2.5" data-testid={testid}>
    <div className="flex flex-wrap items-center gap-2">
      <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${tone}`}><Icon className="h-4 w-4" /> {title}</p>
      {badge}
    </div>
    {children}
  </div>
);

const LearningPlan = ({ ai, lpStatus, badge }) => {
  if (lpStatus === 'pending') {
    return (
      <Block icon={CalendarRange} title="Personalised learning plan" badge={badge} testid="report-learning-plan">
        <p className="flex items-center gap-2 text-sm text-indigo-700" data-testid="report-learning-plan-pending"><Loader2 className="h-4 w-4 animate-spin" /> Writing the learning plan…</p>
      </Block>
    );
  }
  if (!(ai.learning_plan || []).length) {
    return ai.learning_plan_skipped && badge ? (
      <Block icon={CalendarRange} title="Personalised learning plan" badge={badge} testid="report-learning-plan">
        <p className="text-sm text-slate-500" data-testid="report-learning-plan-off">Switched off in Config when this test was graded, so no plan was written. Turn it on to generate one.</p>
      </Block>
    ) : null;
  }
  return (
    <Block icon={CalendarRange} title="Personalised learning plan" badge={badge} testid="report-learning-plan">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ai.learning_plan.map((w) => (
          <div key={w.week} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">{w.week}</p>
            <p className="text-sm font-semibold text-slate-900 mt-0.5">{w.focus}</p>
            <ul className="mt-2 space-y-1">{(w.activities || []).map((x) => <li key={x} className="text-xs text-slate-600">• {x}</li>)}</ul>
          </div>
        ))}
      </div>
    </Block>
  );
};

export const AiInsights = ({ ai, status, visibility = {}, internal = false, lpStatus }) => {
  const hidden = (k) => (internal && visibility[k] === false ? <HiddenBadge testid={`hidden-badge-${k}`} /> : null);
  if (!ai) {
    return (
      <section className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-6 flex items-center gap-3 text-sm text-indigo-700" data-testid="report-ai-pending">
        <Loader2 className="h-4 w-4 animate-spin" />
        {status === 'pending' ? `AI is writing detailed feedback${visibility.learning_plan === false ? '' : ' and a learning plan'}…` : 'AI feedback unavailable.'}
      </section>
    );
  }
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6" data-testid="report-ai-insights">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4" /></span>
        <div>
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI expertise assessment</h3>
          <p className="text-sm text-slate-600 leading-relaxed mt-1" data-testid="report-ai-summary">{ai.summary}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Block icon={ThumbsUp} title="Strengths" tone="text-emerald-700">
          <ul className="space-y-1.5">{(ai.strengths || []).map((s) => <li key={s} className="text-sm text-slate-700 pl-3 border-l-2 border-emerald-300">{s}</li>)}</ul>
        </Block>
        <Block icon={TrendingUp} title="Improvement areas" tone="text-amber-700">
          <ul className="space-y-2.5">
            {(ai.improvement_areas || []).map((a) => (
              <li key={a.skill} className="text-sm">
                <span className="font-semibold text-slate-800">{a.skill}</span>
                <span className="ml-2 text-[11px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">needs {a.gap}%</span>
                <p className="text-slate-600 mt-0.5">{a.advice}</p>
              </li>
            ))}
          </ul>
        </Block>
      </div>
      <LearningPlan ai={ai} lpStatus={lpStatus} badge={hidden('learning_plan')} />
      <div className="grid md:grid-cols-2 gap-6">
        {!!(ai.interview_probes || []).length && (
          <Block icon={MessageCircleQuestion} title="Suggested interview probes" badge={hidden('interview_probes')} testid="report-interview-probes">
            <ol className="space-y-1.5 list-decimal list-inside">{ai.interview_probes.map((q) => <li key={q} className="text-sm text-slate-700">{q}</li>)}</ol>
          </Block>
        )}
        {ai.role_fit && (
          <Block icon={Briefcase} title="Role fit"><p className="text-sm text-slate-700">{ai.role_fit}</p></Block>
        )}
      </div>
    </section>
  );
};

export default AiInsights;
